import { Component } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { AppNotificationService } from '@core/services/app-notification.service';
import { ConfirmDialogService } from '@shared/components/dialogs/confirm-dialog/confirm-dialog.service';
import { BcmService } from '@modules/bcm/bcm.service';
import { Tenant } from '@shared/models/tenant';
import { noop } from 'rxjs';
import { TenantApiService } from '@modules/bcm/@shared/services';
import { TranslationService } from '@core/translation/translation.service';

@Component({
    selector: 'sign-avv',
    templateUrl: './sign-avv.component.html',
    standalone: false
})
export class SignAvvComponent {

    formGroup: UntypedFormGroup;

    tenant: Tenant;

    isSaving: boolean;

    isLoadingAvvPdf: boolean;

    constructor(private _dialogRef: MatDialogRef<SignAvvComponent>,
                private _appNotificationService: AppNotificationService,
                private _confirmDialogService: ConfirmDialogService,
                private _tenantApiService: TenantApiService,
                private _bcmService: BcmService,
                private _formBuilder: UntypedFormBuilder) {

        this._dialogRef.addPanelClass('default-dialog');
        this._dialogRef.disableClose = true;

        this.formGroup = this._formBuilder.group({});

        this._bcmService.selectedTenant$.subscribe(tenant => this.tenant = tenant);
    }

    onClickClose(): void {

        if (this.formGroup.dirty) {
            return this._confirmDialogService
                .setTitle(TranslationService.translate('workflows.screens.signAvv.discardDirtyTitle'))
                .setBody(TranslationService.translate('workflows.screens.signAvv.discardDirtyBody'))
                .setYesButton({
                    color: 'warn',
                    text: TranslationService.translate('common.yes')
                })
                .setNoButton({
                    color: 'accent',
                    text: TranslationService.translate('common.no')
                })
                .openWithCallback(() => this._avvAdvice());
        }

        this._avvAdvice();
    }

    onClickSaveAndClose(): void {

        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            this._appNotificationService.showError(TranslationService.translate('common.checkFieldsHighlight'));
            return;
        }

        this.isSaving = true;

        const {signature, signatureText} = this.formGroup.get('avv').value;
        this._tenantApiService
            .saveAvv(this.tenant, signature, signatureText)
            .subscribe(
                () => {
                    this._appNotificationService.showSuccess(TranslationService.translate('workflows.screens.signAvv.avvSavedSuccess'));
                    this._dialogRef.close('signed');
                },
                () => {
                    this._appNotificationService.showError(TranslationService.translate('workflows.screens.signAvv.avvSaveError'));
                }
            )
            .add(() => this.isSaving = false);
    }

    private _avvAdvice(): void {
        const tenantName = this.tenant?.name ?? '';
        this._confirmDialogService
            .setTitle(TranslationService.translate('workflows.screens.signAvv.postponeAdviceTitle'))
            .setBody(TranslationService.translate('workflows.screens.signAvv.postponeAdviceBodyHtml', { tenantName }))
            .setYesButton({
                color: 'accent',
                text: TranslationService.translate('workflows.screens.signAvv.postponeAdviceConfirmButton')
            })
            .setNoButton({
                text: TranslationService.translate('workflows.screens.signAvv.notNow')
            })
            .openWithCallback(
                () => noop(),
                () => {
                    this._tenantApiService.deferAvv(this.tenant).subscribe(
                        () => this._dialogRef.close('deferred'),
                        () => this._dialogRef.close('deferred'),
                    );
                },
                () => noop(),
            );
    }
}
